import { Helmet } from 'react-helmet';
import { useMemo, useState } from 'react';
import Button from '../components/ui/Button';
import Surface from '../components/ui/Surface';

const leadership = [
  {
    name: 'Logan M. Mercer Jr',
    title: 'Founder | Wealth Advisor',
    certifications: 'UACIC, AWMA, CFP',
    bio: 'Short professional biography to be added.',
    image: null
  },
  {
    name: 'Dawson A. Whitaker',
    title: 'Founder | Wealth Advisor',
    certifications: 'UACIC, AWMA',
    bio: 'Short professional biography to be added.',
    image: null
  }
];

const teamMembers = [
  {
    name: 'Jordan Lee',
    title: 'Senior Advisor',
    certifications: 'CFP®',
    category: 'Advisors',
    image: null
  },
  {
    name: 'Taylor Brooks',
    title: 'Client Service Specialist',
    certifications: '',
    category: 'Support Specialists',
    image: null
  },
  {
    name: 'Morgan Ellis',
    title: 'Chief Operating Officer',
    certifications: '',
    category: 'Executive Team',
    image: null
  },
  {
    name: 'Avery Collins',
    title: 'Operations Associate',
    certifications: '',
    category: 'Staff',
    image: null
  },
  {
    name: 'Reese Bennett',
    title: 'Associate Advisor',
    certifications: 'AWMA',
    category: 'Advisors',
    image: null
  },
  {
    name: 'Parker Hayes',
    title: 'Client Support Associate',
    certifications: '',
    category: 'Support Specialists',
    image: null
  },
  {
    name: 'Blake Morgan',
    title: 'Executive Assistant',
    certifications: '',
    category: 'Staff',
    image: null
  },
  {
    name: 'Cameron Price',
    title: 'Director of Planning',
    certifications: 'CFP®',
    category: 'Executive Team',
    image: null
  }
];

const filters = ['All', 'Advisors', 'Support Specialists', 'Executive Team', 'Staff'];

function SectionDivider({ label }) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-5">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-300 to-transparent" aria-hidden="true" />
      {label ? (
        <h2 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">{label}</h2>
      ) : null}
    </div>
  );
}

function PlaceholderPortrait({ name, className = '' }) {
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-100 via-slate-50 to-white ring-1 ring-slate-200 ${className}`.trim()}
      aria-label={`${name} placeholder portrait`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(43,82,136,0.08),_transparent_55%)]" aria-hidden="true" />
      <div className="flex h-full w-full flex-col items-center justify-center text-slate-400">
        <div className="flex h-20 w-20 items-center justify-center rounded-full border border-slate-300 bg-white/80 text-xl font-semibold tracking-wide">
          {initials}
        </div>
        <span className="mt-4 text-xs font-medium uppercase tracking-[0.22em] text-slate-400">
          Headshot Placeholder
        </span>
      </div>
    </div>
  );
}

function LeadershipCard({ person }) {
  return (
    <Surface className="h-full p-6 md:p-8">
      <div className="grid gap-6 md:grid-cols-[220px_1fr] md:items-start">
        <PlaceholderPortrait name={person.name} className="aspect-[4/5] min-h-[260px]" />
        <div className="flex h-full flex-col">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cam-blue/70">Leadership</p>
            <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">{person.name}</h3>
            <p className="mt-2 text-base font-medium text-slate-700">{person.title}</p>
            <p className="mt-2 text-sm uppercase tracking-[0.16em] text-slate-500">{person.certifications}</p>
            <p className="mt-6 max-w-xl text-sm leading-7 text-slate-600">{person.bio}</p>
          </div>
        </div>
      </div>
    </Surface>
  );
}

function TeamDirectoryCard({ person }) {
  return (
    <Surface className="h-full p-5 md:p-6">
      <div className="flex h-full flex-col">
        <PlaceholderPortrait name={person.name} className="aspect-[4/5] w-full" />
        <div className="mt-5 flex flex-1 flex-col">
          <span className="inline-flex w-fit rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600 ring-1 ring-slate-200">
            {person.category}
          </span>
          <h3 className="mt-4 text-lg font-semibold text-slate-900">{person.name}</h3>
          <p className="mt-1 text-sm text-slate-600">{person.title}</p>
          {person.certifications ? (
            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-cam-blue/70">
              {person.certifications}
            </p>
          ) : null}
        </div>
      </div>
    </Surface>
  );
}

export default function TeamPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [query, setQuery] = useState('');

  const title = 'Our Team | Carter Asset Management';
  const description =
    'Meet the leadership team and extended network of professionals supporting Carter Asset Management clients with a refined, relationship-first approach.';

  const filteredMembers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return teamMembers.filter((person) => {
      const matchesFilter =
        activeFilter === 'All' ? true : person.category === activeFilter;

      const searchableText = [person.name, person.title, person.certifications]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesQuery = normalizedQuery ? searchableText.includes(normalizedQuery) : true;

      return matchesFilter && matchesQuery;
    });
  }, [activeFilter, query]);

  return (
    <main className="section-container py-10 md:py-20">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href="https://carteram.com/team" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Carter Asset Management',
            url: 'https://carteram.com',
            employee: [...leadership, ...teamMembers].map((person) => ({
              '@type': 'Person',
              name: person.name,
              jobTitle: person.title
            }))
          })}
        </script>
      </Helmet>

      <section className="mx-auto max-w-4xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">About</p>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
          Your extended family of financial professionals
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-slate-600 md:text-lg">
          Boutique advice is never built alone. Our firm pairs leadership, service, and operational support to create a thoughtful experience for every household we serve.
        </p>
      </section>

      <section className="mt-12">
        <SectionDivider label="Leadership" />
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {leadership.map((person) => (
          <LeadershipCard key={person.name} person={person} />
        ))}
      </section>

      <section className="mt-16">
        <SectionDivider label="Your Team" />
      </section>

      <section className="mt-10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3">
            {filters.map((filter) => {
              const selected = activeFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setActiveFilter(filter)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cam-blue ${
                    selected
                      ? 'border-cam-blue bg-cam-blue text-white shadow-sm'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {filter}
                </button>
              );
            })}
          </div>

          <div className="w-full lg:max-w-sm">
            <label htmlFor="team-search" className="sr-only">
              Search team members
            </label>
            <input
              id="team-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search team members"
              className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue/20"
            />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {filteredMembers.map((person) => (
            <TeamDirectoryCard key={`${person.name}-${person.category}`} person={person} />
          ))}
        </div>

        {!filteredMembers.length ? (
          <Surface className="mt-8 p-8 text-center">
            <p className="text-base font-medium text-slate-900">No team members match your filters.</p>
            <p className="mt-2 text-sm text-slate-600">Try a different category or search term.</p>
          </Surface>
        ) : null}
      </section>

      <section className="mt-16">
        <Surface className="flex flex-col gap-4 rounded-2xl p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Take the first step</p>
            <h3 className="mt-1 text-base font-semibold text-slate-900 md:text-xl">
              Ready to begin the discovery conversation?
            </h3>
            <p className="mt-1 text-slate-700">
              Start with a 20-minute call. No pressure—just clarity.
            </p>
          </div>
          <Button href="/contact" variant="primary" className="w-full sm:w-auto">
            Schedule a Call
          </Button>
        </Surface>
      </section>

      <p className="mt-6 text-xs text-slate-500">
        Titles and credentials are provided for informational purposes. Advisory services are offered through Carter Asset Management, LLC, a Registered Investment Adviser. Nothing on this page should be construed as a guarantee of future outcomes.
      </p>
    </main>
  );
}
